import { Helmet } from "react-helmet";
import { absoluteUrl, buildTitle, pageSeo, PageSeoKey, seoConfig } from "../seo/config";

type SeoHeadProps = {
  page?: PageSeoKey;
  title?: string;
  description?: string;
  path?: string;
  keywords?: string[];
  noindex?: boolean;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown>;
};

export const SeoHead = ({
  page,
  title,
  description,
  path,
  keywords = [],
  noindex,
  ogType,
  jsonLd,
}: SeoHeadProps) => {
  const configuredPage = page ? pageSeo[page] : undefined;
  const pageTitle = title || configuredPage?.title || "Real Estate & Property in Nigeria for Sale and Rent";
  const pageDescription = description || configuredPage?.description || "Find verified property for rent, sale, and shortlet stays in Nigeria with Krent.";
  const pagePath = path || configuredPage?.path || "/";
  const url = absoluteUrl(pagePath);
  const fullTitle = buildTitle(pageTitle);
  const keywordList = [...new Set([...(configuredPage?.keywords || []), ...keywords, ...seoConfig.defaultKeywords])];
  const image = absoluteUrl(seoConfig.defaultOgImage);
  const shouldNoindex = noindex ?? configuredPage?.noindex ?? false;

  return (
    <Helmet>
      <html lang="en-NG" />
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywordList.join(", ")} />
      <meta name="author" content={seoConfig.siteName} />
      <meta name="robots" content={shouldNoindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"} />
      <meta name="geo.region" content={seoConfig.region} />
      <link rel="canonical" href={url} />

      <meta property="og:locale" content={seoConfig.locale} />
      <meta property="og:type" content={ogType || configuredPage?.ogType || "website"} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={`${seoConfig.siteName} property listings in Nigeria`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
